import { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import api from "../../services/api";
import ModalCustom from "./modal-template-default";
import Slct from "../Slct";
import Required from "../Required";
import useWrapper from "../../hooks/useWrapper";
import getCommercial from "../../util/getCommercial";
import { toast } from "react-toastify";
import Loading from "../Loading";
import Results from "../Results";
import useFormat from "../../hooks/utils/useFormat";
import { traineeFinancial, traineeFinancialGroups, getMultiSlips } from "../../hooks/api/listFinancial";
import { Currency, Percent, InputText, IntegerNumber } from "../Input";
import inputStyle from "../Input/style.module.css";
import Accordion2 from "../Accordion";
import Checkbox from "../Checkbox";
import PackageItemsCloud from "../PackageItemsCloud";
import PackageSelectionCard from "../PackageSelectionCard";
import Button from "../Button";
import TableResponsive from "../TableResponsive";
import DatePicker from "../DatePicker";
import InputWSelect2 from "../InputWSelect2";
import { validateForm } from "../ValidateForm";
import SlipsTable from "../SlipsTable";
import usePermission from "../../hooks/usePermission";
import StatusFlag from "../StatusFlag";
import QuotaEditor from "../QuotaEditor";

const TOLERANCE = 0.01;

// Uma linha por evento que já tem Base de Convites/Ingressos configurada na planilha de custos —
// sem precisar clicar em "Adicionar". A Base fica fixa (já embutida no financeiro via pacote); só o
// Extra (a mais que a Base) é negociável aqui — e soma direto no Total Contratado do formando
// (qtd × valor unitário), sem precisar de um Item Adicional à parte.
function buildEventInvites(events, existingInvites) {
  return (events || [])
    .filter(__event => !!__event.value && (__event.qtd_invitations != null || __event.qtd_tickets != null))
    .map(__event => {
      // Convite e Ingresso são dois Itens Adicionais independentes (um por invite_type) —
      // cada um com seu próprio id e quantidade, já que `quantity` guarda o Extra direto,
      // sem Base pra subtrair.
      const _existing_invitation = existingInvites?.find(
        __invite => __invite.event_id === __event.value && __invite.invite_type === "convite",
      );
      const _existing_ticket = existingInvites?.find(
        __invite => __invite.event_id === __event.value && __invite.invite_type === "ingresso",
      );
      return {
        convite_id: _existing_invitation?.id,
        ingresso_id: _existing_ticket?.id,
        event_id: __event.value,
        event_label: __event.label,
        base_invitations: __event.qtd_invitations || 0,
        base_tickets: __event.qtd_tickets || 0,
        value_invitation: __event.value_invitation || 0,
        value_ticket: __event.value_ticket || 0,
        extra_invitations: _existing_invitation?.quantity || 0,
        extra_tickets: _existing_ticket?.quantity || 0,
      };
    });
}

// Valor dos Extras negociados (Extra × valor unitário) — soma direto no Total Contratado, do
// mesmo jeito que pacote/avulsos, sem passar por Item Adicional/custom-payment.
function eventInvitesExtraValue(eventInvites) {
  return (
    eventInvites?.reduce(
      (__acc, __invite) =>
        __acc +
        (__invite?.extra_invitations || 0) * (__invite?.value_invitation || 0) +
        (__invite?.extra_tickets || 0) * (__invite?.value_ticket || 0),
      0,
    ) || 0
  );
}

export default function ModalFinancialReceipt(props) {
  const {
    show,
    onHide,
    pre_load = {},
    transaction_config = {
      type: "despesa",
      batch: false,
    },
    refreshCallback,
  } = props;

  const { me, config } = useWrapper();
  const { formatReal, formatDate } = useFormat();
  const canCreateFinancialGroup = usePermission("financialGroup", "create");
  const canCreateFinancialCompany = usePermission("financialCompany", "create");
  const canSubmit = transaction_config?.type === "despesa" ? canCreateFinancialCompany : canCreateFinancialGroup;

  const [selects, setselects] = useState({});
  const [costCenters, setcostCenters] = useState([]);
  const [commercials, setcommercials] = useState([]);
  const [suppliers, setsuppliers] = useState();
  const [traineeGroups, settraineeGroups] = useState([]);
  const [packageData, setpackageData] = useState();
  const [packageSelects, setpackageSelects] = useState();
  const [slips, setslips] = useState([]);

  const [rawValueTotal, setrawValueTotal] = useState(0);
  const [refundCostCenters, setrefundCostCenters] = useState([]);
  const [refundSelects, setrefundSelects] = useState({});
  const [refundPaymentType, setrefundPaymentType] = useState();
  const [refundPaymentMethod, setrefundPaymentMethod] = useState();
  const [refundQuotaConfig, setrefundQuotaConfig] = useState({});
  const [refundQuotas, setrefundQuotas] = useState([]);
  const [refundDescription, setrefundDescription] = useState("");

  const willRefund =
    transaction_config?.type === "receita" &&
    !transaction_config?.batch &&
    selects?.trainee_ids?.length === 1 &&
    rawValueTotal < -TOLERANCE;

  const setpackageSelectsArray = props => {
    const { index, data, key } = props;
    setpackageSelects({
      ...packageSelects,
      [key]: packageSelects?.[key]?.map((__item, __i) =>
        __i === index
          ? {
              ...__item,
              ...data,
            }
          : __item,
      ),
    });
  };

  const [isLoadingStartup, setisLoadingStartup] = useState(true);
  const [isLoadingTraineesData, setisLoadingTraineesData] = useState(false);
  const [isLoadingTraineeGroups, setisLoadingTraineeGroups] = useState();
  const [isLoadingRequest, setisLoadingRequest] = useState();
  const [isWaitingResponse, setisWaitingResponse] = useState(false);

  useEffect(() => {
    setisWaitingResponse(isLoadingStartup || isLoadingTraineesData || isLoadingTraineeGroups || isLoadingRequest);
  }, [isLoadingStartup, isLoadingTraineesData, isLoadingTraineeGroups, isLoadingRequest]);

  // On Load Data
  useEffect(() => {
    !!show &&
    (transaction_config?.type === "receita" ||
      transaction_config?.type === "despesa" ||
      transaction_config?.type === "gerar-diferenca")
      ? (async () => {
          setisLoadingStartup(true);
          try {
            const { data: _cost_centers } = await api.get(
              `cost-center?type=${transaction_config?.type === "despesa" ? "despesa" : "receita"}`,
              config,
            );
            setcostCenters(
              _cost_centers?.map(__cost_center => ({
                value: __cost_center.id,
                label: __cost_center.name,
                expensePlan: __cost_center.expensePlan?.map(__expense_plan => ({
                  value: __expense_plan.id,
                  label: __expense_plan.name,
                  expenses: __expense_plan.expenses?.map(__expense => ({
                    value: __expense.id,
                    label: __expense.name,
                  })),
                })),
              })),
            );

            if (transaction_config?.type === "receita") {
              const { data: _refund_cost_centers } = await api.get("cost-center?type=despesa", config);
              setrefundCostCenters(
                _refund_cost_centers?.map(__cost_center => ({
                  value: __cost_center.id,
                  label: __cost_center.name,
                  expensePlan: __cost_center.expensePlan?.map(__expense_plan => ({
                    value: __expense_plan.id,
                    label: __expense_plan.name,
                    expenses: __expense_plan.expenses?.map(__expense => ({
                      value: __expense.id,
                      label: __expense.name,
                    })),
                  })),
                })),
              );
            }

            const _commercials = await getCommercial();
            setcommercials(
              _commercials.map(__group => {
                const _disabled_message = !!pre_load.trainee_ids
                  ? ["", ""]
                  : !__group.has_trainee_package
                    ? ["- ", "(0 Formandos)"]
                    : ["", ""];
                return {
                  label: `${_disabled_message[0]} ${__group.label} ${_disabled_message[1]}`,
                  value: __group.value,
                  disabled: !__group.has_trainee_package,
                  trainees: __group.trainees?.map(__trainee => {
                    const _disabled_message = !!pre_load.trainee_ids
                      ? ["", ""]
                      : !!__trainee.financial_released
                        ? ["- ", " (Financeiro Criado)"]
                        : ["", ""];
                    return {
                      label: `${_disabled_message[0]} ${__trainee.name} ${_disabled_message[1]}`,
                      value: __trainee.id,
                      disabled: !!transaction_config?.batch && !!__trainee.financial_released,
                    };
                  }),
                };
              }),
            );
            if (transaction_config?.type === "despesa") {
              const { data: _suppliers } = await api.get("providers", config);
              setsuppliers(
                _suppliers.map(__supplier => ({
                  value: __supplier.id,
                  label: __supplier.name,
                })),
              );
            }
            if (transaction_config?.type === "gerar-diferenca" && !!pre_load?.value) {
              setvalueNegotiation(pre_load?.value || 0);
            } else {
              setvalueNegotiation();
            }
            setselects(pre_load);
            setvalues({});
            setvaluesPercent({});
            settraineeGroups([]);
            setpackageData();
            setpackageSelects();
            setpackageData();
            setrawValueTotal(0);
            setrefundSelects({});
            setrefundPaymentType();
            setrefundPaymentMethod();
            setrefundQuotaConfig({ quota_value: 1, first_quota_date: null, quota_interval: null, has_entry: false });
            setrefundQuotas([]);
            setrefundDescription("");
          } catch (__error) {
            toast.error("Erro ao tentar carregar alguns dados");
            console.log("__error", __error);
          } finally {
            setisLoadingStartup(false);
          }
        })()
      : transaction_config?.type === "reboleto" && pre_load?.payment_slip_ids?.length > 0
        ? (async () => {
            setisLoadingStartup(true);
            try {
              const _slips = await getMultiSlips({
                slips: pre_load?.payment_slip_ids,
                config,
                me,
              });
              const _trainee_id = _slips?.[0]?.trainee?.id || null;
              if (_slips?.some(__slip => __slip?.trainee?.id !== _trainee_id) && !!_trainee_id) {
                toast.error("Você está tentando modificar boletos de formandos diferentes.");
                resetDefaultSelects();
                return;
              }
              if (_slips?.some(__slip => __slip.status === "Pago" || __slip.status === "Cancelado")) {
                toast.error("Você está tentando modificar um boleto que não está Ativo.");
                resetDefaultSelects();
                return;
              }
              setslips(_slips);
              setselects(pre_load);
              setvalues({});
              setvaluesPercent({});
              settraineeGroups([]);
              setpackageData();
              setpackageSelects();
              setpackageData();
            } catch (__error) {
              toast.error("Erro ao tentar carregar os dados do(s) boleto(s)");
              console.log("__error", __error);
            } finally {
              setisLoadingStartup(false);
            }
          })()
        : transaction_config?.type === "gerar-diferenca"
          ? (async () => {
              setisLoadingStartup(false);
            })()
          : setisLoadingStartup(false);
    // config/me/pre_load/transaction_config/resetDefaultSelects are recreated every render (context values
    // and default-valued props); this effect is intentionally keyed to the show transition only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Get Trainees Values (Payment Slips)
  const selectsTraineeIdsKey = JSON.stringify(selects?.trainee_ids);
  useEffect(() => {
    if (!!selects?.trainee_ids) {
      (async () => {
        setisLoadingTraineesData(true);
        try {
          if (!transaction_config?.batch && transaction_config?.type === "receita") {
            for (const __trainee_id of selects?.trainee_ids || []) {
              const _trainee_financial = await traineeFinancial({
                me,
                config,
                trainee_id: __trainee_id,
                commercial_id: selects?.commercial_id,
              });
              const _trainee_values = _trainee_financial.values_calc?.contracted;
              const _commercial = _trainee_financial?.commercial;

              setpackageData({
                packages: _commercial?.packages_data?.packages || null,
                singles: _commercial?.packages_data?.singles || [],
                events: _commercial?.packages_data?.events || [],
                date_end: _commercial?.date_end,
              });
              setpackageSelects({
                package: _trainee_financial?.package?.id || null,
                singles: _trainee_financial?.singles?.map(__single => __single?.id) || [],
                discounts: _trainee_financial?.discounts || [],
                event_invites: buildEventInvites(_commercial?.packages_data?.events, _trainee_financial?.event_invites),
                aditional_items: _trainee_financial?.aditional_items || [],
                due_date: _trainee_financial?.due_date || null,
                contributions_benefits: _trainee_values?.contributions_benefits || 0,
                payments_made: _trainee_values?.payments_made || 0,
                refunds_to_trainee: _trainee_values?.refunds_to_trainee || 0,
                payments_made_adjusts: _trainee_values?.payments_made_adjusts || 0,
                slips_fines_fees: _trainee_values?.slips_fines_fees || 0,
              });
            }
          } else if (!transaction_config?.batch && transaction_config?.type === "gerar-diferenca") {
            // Só precisamos do date_end da turma aqui (para o aviso de parcela após o prazo
            // final) — não usar o bloco de "receita" acima, que também popula packageSelects
            // e dispara o efeito que recalcula valueNegotiation a partir de pacote/descontos,
            // sobrescrevendo o valor da diferença já definido em pre_load.value.
            for (const __trainee_id of selects?.trainee_ids || []) {
              const _trainee_financial = await traineeFinancial({
                me,
                config,
                trainee_id: __trainee_id,
                commercial_id: selects?.commercial_id,
              });
              setpackageData({ date_end: _trainee_financial?.commercial?.date_end });
            }
          }
        } catch (__error) {
          toast.error("Erro ao tentar carregar os dados dos formandos.");
          console.log("__error", __error);
        } finally {
          setisLoadingTraineesData(false);
        }
      })();
    }
    // config/me/transaction_config are recreated every render; effect is intentionally keyed to the
    // content of selects.trainee_ids (via selectsTraineeIdsKey) plus commercial_id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectsTraineeIdsKey, selects?.commercial_id]);

  useEffect(() => {
    (async () => {
      if (!!selects?.commercial_id && transaction_config?.type === "receita" && !!transaction_config?.batch) {
        try {
          setisLoadingTraineeGroups(true);
          const _trainees_groups = await traineeFinancialGroups({
            commercial_id: selects?.commercial_id,
          });
          settraineeGroups(_trainees_groups?.groups);
          setpackageData({
            packages: _trainees_groups?.commercial?.packages_data?.packages || null,
            singles: _trainees_groups?.commercial?.packages_data?.singles || [],
            events: _trainees_groups?.commercial?.packages_data?.events || [],
            date_end: _trainees_groups?.commercial?.date_end,
          });
        } catch (__error) {
          toast.error("Erro ao tentar carregar os grupos de formandos.");
          console.log("__error", __error);
        } finally {
          setisLoadingTraineeGroups(false);
        }
      }
    })();
    // transaction_config is recreated every render (default-valued prop); effect intentionally keyed
    // to commercial_id only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selects?.commercial_id]);

  useEffect(() => {
    if (
      (selects?.trainee_group !== null || selects?.trainee_group !== undefined) &&
      transaction_config?.type === "receita" &&
      !!transaction_config?.batch
    ) {
      const _singles = selects?.trainee_group?.split("-")?.map(__single => parseInt(__single)) || null;
      const _package = (!!_singles ? _singles?.splice(0, 1)?.filter(__single => !!__single)?.[0] : null) || null;
      setpackageSelects({
        package: _package,
        singles: _singles,
        discounts: [],
        event_invites: [],
        aditional_items: [],
        due_date: null,
        contributions_benefits: 0,
        payments_made: 0,
        slips_fines_fees: 0,
      });
    }
    // transaction_config is recreated every render (default-valued prop); effect intentionally keyed
    // to trainee_group only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selects?.trainee_group]);

  const [values, setvalues] = useState({});
  const [valuesPercent, setvaluesPercent] = useState({});
  const [valueNegotiation, setvalueNegotiation] = useState();

  useEffect(() => {
    let _discount =
      (values?.discount_type !== "absolute"
        ? valueNegotiation * (valuesPercent?.discount / 100) || 0
        : values?.discount) || 0;
    let _fees =
      (values?.fees_type !== "absolute"
        ? (valueNegotiation - _discount) * (valuesPercent?.fees / 100)
        : values?.fees) || 0;
    let _result = valueNegotiation - _discount + _fees || 0;

    setvalues(v => ({
      ...v,
      discount: _discount || null,
      fees: _fees || null,
      result: _result,
    }));
  }, [
    valueNegotiation,
    valuesPercent?.discount,
    valuesPercent?.fees,
    values?.discount,
    values?.fees,
    values?.discount_type,
    values?.fees_type,
  ]);

  useEffect(() => {
    const _package_value =
      packageData?.packages?.find(__package => __package?.id === packageSelects?.package)?.value_per_trainee_original ||
      0;

    const _singles_value =
      packageSelects?.singles?.reduce(
        (__acc, __single_id) =>
          __acc + packageData?.singles?.find(__single => __single?.id === __single_id)?.value_per_trainee_original,
        0,
      ) || 0;

    const _discounts_value =
      packageSelects?.discounts
        ?.filter(__discount => !__discount.disabled)
        ?.reduce((__acc, __discount) => __acc + (__discount?.value || 0), 0) || 0;

    const _aditional_items_value =
      packageSelects?.aditional_items
        ?.filter(__item => !__item.disabled)
        ?.reduce(
          (__acc, __item) => __acc + ((__item?.value || 0) * (__item?.quantity || 0) + (__item?.convenience_fee || 0)),
          0,
        ) || 0;

    const _payments_made = packageSelects?.payments_made || 0;
    const _refunds_to_trainee = packageSelects?.refunds_to_trainee || 0;
    const _payments_made_adjusts = packageSelects?.payments_made_adjusts || 0;
    const _benefits_value = packageSelects?.contributions_benefits || 0;
    const _slips_fines_fees = packageSelects?.slips_fines_fees || 0;
    const _event_invites_value = eventInvitesExtraValue(packageSelects?.event_invites);

    const _contracted_value = _package_value + _singles_value + _aditional_items_value + _event_invites_value;

    const _value_total =
      _contracted_value -
      _benefits_value -
      _discounts_value -
      _payments_made +
      _refunds_to_trainee -
      _payments_made_adjusts +
      _slips_fines_fees;

    setrawValueTotal(_value_total);
    setvalueNegotiation(_value_total > 0 ? _value_total : 0);
    setvalues(v => ({
      ...v,
      trainee: {
        package_value: _package_value || 0,
        singles_value: _singles_value || 0,
        aditional_items_value: _aditional_items_value || 0,
        event_invites_value: _event_invites_value || 0,
        contracted_value: _contracted_value || 0,
        contributions_benefits: _benefits_value || 0,
        discounts: _discounts_value || 0,
        payments_made: _payments_made || 0,
        refunds_to_trainee: _refunds_to_trainee || 0,
        payments_made_adjusts: _payments_made_adjusts || 0,
        slips_fines_fees: _slips_fines_fees || 0,
      },
    }));
  }, [packageSelects, packageData?.packages, packageData?.singles]);

  const [quotas, setquotas] = useState([]);
  const isNotCardPayment = values?.payment_method !== "cartao";
  useEffect(() => {
    if (values?.result > 0 && !!values?.payment_type && values?.quota_value >= 1 && !!values?.first_quota_date) {
      let _quotas = [];
      const today = new Date();

      let _label_postfix =
        transaction_config?.type === "reboleto"
          ? " (Reboleto)"
          : transaction_config?.type === "gerar-diferenca"
            ? " (Diferença)"
            : "";

      // Entry
      if (values?.has_entry) {
        _quotas.push({
          label: `Entrada${_label_postfix}`,
          value: null,
          due_date: today,
          description: "",
          is_entry: true,
          editable: true,
        });
      }

      // Quotas
      const _quota_init = {
        value: values?.result / values?.quota_value || 0,
        fees: values?.fees / values?.quota_value || 0,
        discount: values?.discount / values?.quota_value || 0,
      };

      const _due_date_day = selects?.due_date || packageSelects?.due_date || values?.first_quota_date.getDate();

      for (let __i = 0; __i < values?.quota_value; __i++) {
        let _due_date = !(values?.quota_interval > 0)
          ? new Date(values?.first_quota_date.getFullYear(), values?.first_quota_date.getMonth() + __i, _due_date_day)
          : new Date(
              values?.first_quota_date.getFullYear(),
              values?.first_quota_date.getMonth(),
              values?.first_quota_date?.getDate() + __i * values?.quota_interval,
            );
        _due_date = _due_date < today ? today : _due_date;
        let _label =
          values?.payment_type === "avista" ? "A Vista" : `${__i + 1}/${values?.quota_value}${_label_postfix}`;
        _quotas.push({
          label: _label,
          due_date: values?.payment_type === "avista" ? today : _due_date,
          description: "",
          value: _quota_init.value,
          fees: _quota_init.fees,
          discount: _quota_init.discount,
        });
      }
      setquotas(_quotas);
    } else {
      setquotas([]);
    }
  }, [
    values?.result,
    values?.payment_type,
    values?.quota_value,
    values?.first_quota_date,
    values?.quota_interval,
    values?.has_entry,
    values?.discount,
    values?.fees,
    selects?.due_date,
    packageSelects?.due_date,
    transaction_config?.type,
    isNotCardPayment,
  ]);

  const checkQuotas = (__value, __index) => {
    let _quotas = [...quotas];
    _quotas[__index].value = __value;
    const _editable_sum = _quotas
      ?.filter(__quota => !!__quota?.editable)
      ?.reduce((__acc, __quota) => __acc + (__quota?.value || 0), 0);
    const _rest = values?.result - _editable_sum;
    const _fees_per_quota = (values?.fees || 0) / _quotas?.length || 0;

    return {
      rest: _rest,
      fees_per_quota: _fees_per_quota,
    };
  };

  const checkQuotaInput = (__value, __index) => {
    const { rest } = checkQuotas(__value, __index);

    if (rest < 0) {
      toast.error(`A soma das parcelas não pode ultrapassar o Resultado da Negociação! ${formatReal(values?.result)}`, {
        toastId: "sum_max",
      });
      return false;
    } else {
      return true;
    }
  };

  const checkQuotaBlur = (__value, __index) => {
    const { fees_per_quota } = checkQuotas(__value, __index);
    if (quotas?.some(__quota => Number((__quota?.value || 0).toFixed(2)) < Number(fees_per_quota.toFixed(2)))) {
      setquotas(
        quotas.map((__quota, __i) =>
          __i === __index
            ? {
                ...__quota,
                value: fees_per_quota,
              }
            : __quota,
        ),
      );
      toast.error(
        `Não pode existir um valor de parcela menor que o valor fracionado das taxas por parcela! 
        ${formatReal(fees_per_quota)}`,
        { toastId: "tax_min" },
      );
      return false;
    } else {
      return true;
    }
  };

  const editableQuotasKey = JSON.stringify(quotas?.filter(__quota => __quota?.editable));
  useEffect(() => {
    if (quotas?.length > 0) {
      const _editable_sum = quotas
        ?.filter(__quota => !!__quota.editable)
        ?.reduce((__acc, __quota) => __acc + (__quota?.value || 0), 0);
      const _not_editable_length = quotas?.filter(__quota => !__quota.editable)?.length;
      const _rest = values?.result - _editable_sum;
      setquotas(
        quotas.map(__quota =>
          !__quota.editable
            ? {
                ...__quota,
                value: _rest / _not_editable_length,
              }
            : __quota,
        ),
      );
    }
    // 'quotas' intentionally excluded: this effect updates quotas itself via setquotas, so depending
    // on the full array would create an infinite render loop; editableQuotasKey is the deliberate trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editableQuotasKey, values?.result]);

  const slipsKey = JSON.stringify(slips);
  useEffect(() => {
    if (slips?.length > 0) {
      let slips_sum = {
        value: 0,
        juros: 0,
        discount: 0,
        fees: 0,
      };
      for (const __slip of slips) {
        slips_sum = {
          value: slips_sum.value + (__slip.value_raw || 0),
          juros: slips_sum.juros + (__slip?.value_juros || 0),
          discount: slips_sum.discount + (__slip.discount || 0),
          fees: slips_sum.fees + (__slip.tax || 0),
        };
      }
      setvalueNegotiation(slips_sum?.value + slips_sum?.juros);
      setvalues(v => ({
        ...v,
        discount_type: "absolute",
        discount: slips_sum?.discount || 0,
        discount_desc: slips_sum?.discount > 0 ? "Desconto cumulativo de boleto(s) renegociado(s)" : "",
        fees_type: "absolute",
        fees: slips_sum?.fees || 0,
        fees_desc: slips_sum?.fees > 0 ? "Taxa cumulativa de boleto(s) renegociado(s)" : "",
        slips: {
          slips_value: slips_sum.value,
          slips_fines_fees: slips_sum.juros,
        },
      }));
    }
    // 'slips' intentionally tracked via slipsKey (content-based) rather than array reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slipsKey]);

  const sendFinancial = async () => {
    if (!canSubmit) return;
    const _is_valid = validateForm(document.getElementById("modal_financial_receipt"));
    if (!_is_valid) return;

    if (willRefund) {
      if (!(refundQuotas?.length > 0)) {
        toast.error("Configure o parcelamento do reembolso antes de salvar.");
        return;
      }
      if (refundQuotas.some(__quota => !(__quota.value > 0) || !__quota.due_date)) {
        toast.error("Toda parcela do reembolso precisa de valor e vencimento.");
        return;
      }
      if (!refundPaymentMethod) {
        toast.error("Selecione o método de pagamento do reembolso.");
        return;
      }
      if (!refundSelects?.cost_center_id) {
        toast.error("Selecione o Centro de Custo do reembolso.");
        return;
      }
    }

    const { type } = transaction_config;
    setisLoadingRequest(true);
    let _quotas = quotas.map((__quota, __quota_i) => {
      let _quotas_data = {
        type_payment: values.payment_type,
        method_payment: values.payment_method,
        total_quotas: quotas.length,
        discount_description: values.discount_desc || "",
        tax_description: values.fees_desc || "",
        quotas: __quota_i + 1,
        label: __quota.label,
        value: __quota.value,
        value_raw: __quota.value + __quota.discount - __quota.fees || __quota.value || 0,
        value_discount: __quota.discount || 0,
        tax: __quota.fees || 0,
        entry: __quota.is_entry || false,
        due_date: __quota.due_date,
        description: __quota.description,
        status: "nao-gerado",
        aporte: false,
      };
      return _quotas_data;
    });
    // Receipt
    if (type === "receita") {
      for (const __trainee of selects?.trainee_ids) {
        try {
          let _trainee_id = __trainee?.value || __trainee;
          _quotas = _quotas.map((__quota, __quota_i) => {
            let _due_date = new Date(
              __quota.due_date.getFullYear(),
              __quota.due_date.getMonth(),
              __trainee?.due_date || __quota.due_date.getDate() || 1,
            );
            let _quotas_data = {
              ...__quota,
              cost_center_id: selects.cost_center_id,
              expense_plan_id: selects.expense_plan_id,
              expense_id: selects.expense_id,
              commercial_id: selects.commercial_id,
              trainee_id: _trainee_id,
              due_date: _due_date,
            };
            return _quotas_data;
          });

          // Packages, Singles & Due Date
          await api.put(
            `trainees/${_trainee_id}`,
            {
              packageList: !!packageSelects?.package ? [packageSelects?.package] : [],
              packageListSingle: !!packageSelects?.singles?.length > 0 ? packageSelects?.singles : [],
            },
            config,
          );

          // Aditional Itens
          const _aditional_item =
            packageSelects.aditional_items?.filter(__item => !!__item.id || !__item.disabled) || [];
          for (const __item of _aditional_item) {
            const _item_to_send = {
              ...(__item.id ? { id: __item.id } : {}),
              payer_type: "item_adicional",
              trainees: [_trainee_id],
              commercial_id: selects.commercial_id,
              cost_center_id: __item.cost_center_id,
              expense_plan_id: __item.expense_plan_id,
              expense_id: __item.expense_id,
              event_id: __item.event_id,
              resource_id: __item.resource_id,
              description: __item.description,
              quantity: __item.quantity,
              value: __item.value,
              convenience_fee: __item.convenience_fee,
              name: __item.name,
              disabled: !!__item.disabled,
            };
            !!__item.id
              ? await api.put(`custom-payment/${__item.id}`, _item_to_send, config)
              : await api.post("custom-payment", _item_to_send, config);
          }

          // Discounts
          const _discounts = packageSelects.discounts?.filter(__item => !!__item.id || !__item.disabled) || [];
          for (const __discount of _discounts) {
            const _discount_to_send = {
              user_id: me?.id,
              trainee_id: _trainee_id,
              commercial_id: selects.commercial_id,
              description: __discount?.description || "",
              value: __discount?.value || 0,
              disabled: !!__discount?.disabled,
            };
            !!__discount.id
              ? await api.put(`discount-trainee/${__discount.id}`, _discount_to_send, config)
              : await api.post("discount-trainee", _discount_to_send, config);
          }

          // Convites e Ingressos Extras por Evento — cada tipo é um Item Adicional próprio
          // (invite_type "convite"/"ingresso"), igual a qualquer outro item adicional. `quantity`
          // pode ser negativo (até o limite de -Base): é como o formando abre mão de parte ou de
          // todo o Base sem "usar" — desconta do contratado exatamente esse valor, em vez de só
          // ajustar a contagem. Só grava quando há de fato um ajuste (positivo ou negativo); volta
          // a ficar em silêncio (soft-delete) se o extra for zerado de novo.
          const _invite_rows = [];
          for (const __invite of packageSelects.event_invites || []) {
            if (!!__invite?.convite_id || (__invite?.extra_invitations || 0) !== 0) {
              _invite_rows.push({
                id: __invite?.convite_id,
                event_id: __invite?.event_id,
                invite_type: "convite",
                quantity: __invite?.extra_invitations || 0,
                value: __invite?.value_invitation || 0,
              });
            }
            if (!!__invite?.ingresso_id || (__invite?.extra_tickets || 0) !== 0) {
              _invite_rows.push({
                id: __invite?.ingresso_id,
                event_id: __invite?.event_id,
                invite_type: "ingresso",
                quantity: __invite?.extra_tickets || 0,
                value: __invite?.value_ticket || 0,
              });
            }
          }
          for (const __row of _invite_rows) {
            const _item_to_send = {
              payer_type: "item_adicional",
              trainees: [_trainee_id],
              commercial_id: selects.commercial_id,
              event_id: __row.event_id,
              invite_type: __row.invite_type,
              name: __row.invite_type === "convite" ? "Convite Extra" : "Ingresso Extra",
              quantity: __row.quantity,
              value: __row.value,
              convenience_fee: 0,
              disabled: __row.quantity === 0,
            };
            !!__row.id
              ? await api.put(`custom-payment/${__row.id}`, _item_to_send, config)
              : await api.post("custom-payment", _item_to_send, config);
          }

          // Trainee Quotas
          await api.post(
            `/paymentslips/edit-negotiation/${_trainee_id}`,
            {
              payments: _quotas,
              ...(willRefund
                ? {
                    refund: {
                      cost_center_id: refundSelects?.cost_center_id,
                      expense_plan_id: refundSelects?.expense_plan_id,
                      expense_id: refundSelects?.expense_id,
                      type_payment: refundPaymentType,
                      method_payment: refundPaymentMethod,
                      description: refundDescription,
                      installments: refundQuotas.map(__quota => ({
                        due_date: __quota.due_date,
                        value: __quota.value,
                        description: __quota.description || null,
                        method_payment: refundPaymentMethod,
                      })),
                    },
                  }
                : {}),
            },
            config,
          );
          refreshCallback();
          toast.success(
            willRefund
              ? "Negociação gerada com sucesso — reembolso registrado ao formando!"
              : "Negociação gerado com sucesso!",
            { toastId: "financial_released" },
          );
        } catch (__error) {
          console.log("__error", __error);
          toast.error(`Erro ao tentar gerar o financeiro do formando ${__trainee}.`);
        }
      }
    }
    // Gerar Diferença
    else if (type === "gerar-diferenca") {
      for (const __trainee of selects?.trainee_ids) {
        try {
          let _trainee_id = __trainee?.value || __trainee;
          _quotas = _quotas.map((__quota, __quota_i) => {
            let _due_date = new Date(
              __quota.due_date.getFullYear(),
              __quota.due_date.getMonth(),
              __trainee?.due_date || __quota.due_date.getDate() || 1,
            );
            let _quotas_data = {
              ...__quota,
              cost_center_id: selects.cost_center_id,
              expense_plan_id: selects.expense_plan_id,
              expense_id: selects.expense_id,
              commercial_id: selects.commercial_id,
              trainee_id: _trainee_id,
              due_date: _due_date,
            };
            return _quotas_data;
          });

          // Trainee Quotas
          await api.post(
            `/paymentslips/lote`,
            {
              payments: _quotas,
            },
            config,
          );
          refreshCallback();
          toast.success("Negociação gerado com sucesso!", { toastId: "financial_released" });
        } catch (__error) {
          console.log("__error", __error);
          toast.error(`Erro ao tentar gerar o financeiro do formando ${__trainee}.`);
        }
      }
    }
    // Reboleto
    else if (type === "reboleto") {
      try {
        const { trainee, commercial, cost_center, expense_plan, expense } = slips?.[0];

        if (!trainee?.id || !commercial?.id) {
          toast.error(`Formando relacionado aos pagamentos não encontrado.`);
          return;
        }
        _quotas = _quotas.map((__quota, __quota_i) => {
          let _quotas_data = {
            ...__quota,
            cost_center_id: cost_center?.id,
            expense_plan_id: expense_plan?.id,
            expense_id: expense?.id,
            commercial_id: commercial?.id,
            trainee_id: trainee?.id,
          };
          return _quotas_data;
        });
        const _old_boletos = slips?.map(__slip => __slip.id);
        await api.post(
          `/paymentslips/renegotiation`,
          {
            newBoletos: _quotas,
            oldBoletos: _old_boletos,
          },
          config,
        );
        refreshCallback();
        toast.success("Renegociação finalizada com sucesso!", { toastId: "financial_released" });
      } catch (__error) {
        console.log("__error", __error);
        toast.error(`Erro ao tentar renegociar os pagamentos.`);
      }
    }
    // Expenses
    else if (type === "despesa") {
    }
    // Error
    else {
      toast.error("Algo errado aconteceu. Entre em contato com a nossa equipe.");
    }
    setisLoadingRequest(false);
    resetDefaultSelects();
  };

  const resetDefaultSelects = __ev => {
    onHide();
    setselects(__ev || {});
    setvalues({});
    setvaluesPercent({});
    setslips([]);
    setvalueNegotiation();
    setpackageData();
    setpackageSelects();
    settraineeGroups([]);
    setisLoadingRequest(false);
    setisLoadingStartup(false);
    setisLoadingTraineesData(false);
    setisLoadingTraineeGroups(false);
  };

  // Nota de Convite/Ingresso (valor unitário) deste evento, se tiver Base configurada — some
  // dentro do próprio chip do evento, uma linha abaixo, em vez de virar chips separados.
  const buildInviteNote = event_id => {
    const _invite = packageSelects?.event_invites?.find(__invite => __invite?.event_id === event_id);
    if (!_invite) return null;

    const _parts = [];
    if (!!_invite.value_invitation) _parts.push(`Convite: ${formatReal(_invite.value_invitation)}/un.`);
    if (!!_invite.value_ticket) _parts.push(`Ingresso: ${formatReal(_invite.value_ticket)}/un.`);
    return _parts.length > 0 ? _parts.join(" · ") : null;
  };

  // Tabela de Convites/Ingressos (Base + Extra), escopada aos eventos que pertencem a este
  // pacote/avulso específico — em vez de uma lista única com todos os eventos da turma.
  const renderEventInvitesTable = events => {
    const _event_ids = new Set((events || []).map(__event => __event?.id));
    const _invites =
      packageSelects?.event_invites
        ?.map((__invite, __invite_i) => ({ invite: __invite, index: __invite_i }))
        ?.filter(({ invite }) => _event_ids.has(invite?.event_id)) || [];

    if (_invites.length === 0) return null;

    // Com mais de um evento na mesma tabela (pacote com vários eventos), a coluna de Evento
    // volta — senão não dá pra saber a qual evento cada linha de convites/ingressos pertence.
    const _show_event_column = _invites.length > 1;

    return (
      <>
        <div className="divisor mt-3 mb-3"></div>
        <TableResponsive
          table={{
            head: [
              ...(_show_event_column ? [{ data: "Evento", className: "align-middle" }] : []),
              { data: "Convites Base", className: "align-middle" },
              { data: "Convites Extras", className: "align-middle" },
              { data: "Convites Total", className: "align-middle" },
              { data: "Ingressos Base", className: "align-middle" },
              { data: "Ingressos Extras", className: "align-middle" },
              { data: "Ingressos Total", className: "align-middle" },
            ],
            body: _invites.map(({ invite: __invite, index: __invite_i }) => ({
              data: [
                ...(_show_event_column
                  ? [{ element: <span>{__invite?.event_label}</span>, className: "align-middle" }]
                  : []),
                {
                  element: <IntegerNumber disabled={true} value={__invite?.base_invitations} />,
                  className: "align-middle",
                },
                {
                  className: "align-middle",
                  element: (
                    <div className={inputStyle.input_time}>
                      <button
                        type="button"
                        disabled={!!isWaitingResponse}
                        className={inputStyle.button_multiplier}
                        onClick={() => {
                          const _current = __invite?.extra_invitations || 0;
                          const _flipped = Math.max(-_current, -(__invite?.base_invitations || 0));
                          setpackageSelectsArray({
                            key: "event_invites",
                            index: __invite_i,
                            data: { extra_invitations: _flipped },
                          });
                        }}
                      >
                        <span data-on={(__invite?.extra_invitations || 0) > 0}>+</span>
                        <span data-on={(__invite?.extra_invitations || 0) < 0}>-</span>
                      </button>
                      <IntegerNumber
                        disabled={!!isWaitingResponse}
                        allowNegative={false}
                        style={{ paddingLeft: "2.25rem" }}
                        value={Math.abs(__invite?.extra_invitations || 0)}
                        // Só limita a magnitude quando o sinal é negativo (abrindo mão do Base) —
                        // rejeita a tecla na hora, em vez de deixar digitar e corrigir depois: o
                        // NumericFormat não resincroniza o texto exibido com o valor corrigido
                        // enquanto o campo está focado, então corrigir só no state deixava o
                        // número errado visível até o próximo toque.
                        isAllowed={({ floatValue }) => {
                          const _isNegativeMode = (__invite?.extra_invitations || 0) < 0;
                          if (!_isNegativeMode) return true;
                          return !floatValue || floatValue <= (__invite?.base_invitations || 0);
                        }}
                        valueChange={__ev => {
                          const _sign = (__invite?.extra_invitations || 0) < 0 ? -1 : 1;
                          const _signed = (parseInt(__ev) || 0) * _sign;
                          const _clamped = Math.max(_signed, -(__invite?.base_invitations || 0));
                          setpackageSelectsArray({
                            key: "event_invites",
                            index: __invite_i,
                            data: { extra_invitations: _clamped },
                          });
                        }}
                      />
                    </div>
                  ),
                },
                {
                  className: "align-middle",
                  element: (
                    <IntegerNumber
                      disabled={true}
                      value={(__invite?.base_invitations || 0) + (__invite?.extra_invitations || 0)}
                    />
                  ),
                },
                {
                  element: <IntegerNumber disabled={true} value={__invite?.base_tickets} />,
                  className: "align-middle",
                },
                {
                  className: "align-middle",
                  element: (
                    <div className={inputStyle.input_time}>
                      <button
                        type="button"
                        disabled={!!isWaitingResponse}
                        className={inputStyle.button_multiplier}
                        onClick={() => {
                          const _current = __invite?.extra_tickets || 0;
                          const _flipped = Math.max(-_current, -(__invite?.base_tickets || 0));
                          setpackageSelectsArray({
                            key: "event_invites",
                            index: __invite_i,
                            data: { extra_tickets: _flipped },
                          });
                        }}
                      >
                        <span data-on={(__invite?.extra_tickets || 0) > 0}>+</span>
                        <span data-on={(__invite?.extra_tickets || 0) < 0}>-</span>
                      </button>
                      <IntegerNumber
                        disabled={!!isWaitingResponse}
                        allowNegative={false}
                        style={{ paddingLeft: "2.25rem" }}
                        value={Math.abs(__invite?.extra_tickets || 0)}
                        isAllowed={({ floatValue }) => {
                          const _isNegativeMode = (__invite?.extra_tickets || 0) < 0;
                          if (!_isNegativeMode) return true;
                          return !floatValue || floatValue <= (__invite?.base_tickets || 0);
                        }}
                        valueChange={__ev => {
                          const _sign = (__invite?.extra_tickets || 0) < 0 ? -1 : 1;
                          const _signed = (parseInt(__ev) || 0) * _sign;
                          const _clamped = Math.max(_signed, -(__invite?.base_tickets || 0));
                          setpackageSelectsArray({
                            key: "event_invites",
                            index: __invite_i,
                            data: { extra_tickets: _clamped },
                          });
                        }}
                      />
                    </div>
                  ),
                },
                {
                  className: "align-middle",
                  element: (
                    <IntegerNumber
                      disabled={true}
                      value={(__invite?.base_tickets || 0) + (__invite?.extra_tickets || 0)}
                    />
                  ),
                },
              ],
            })),
          }}
        />
      </>
    );
  };

  return (
    <ModalCustom
      {...props}
      size="xl"
      show={show}
      onHide={() => {
        resetDefaultSelects();
      }}
      disableClose={!!isWaitingResponse}
      id="modal_financial_receipt"
    >
      <ModalCustom.Header>
        <ModalCustom.Title>
          {transaction_config?.type === "receita" ? (
            <>
              {canSubmit ? "Gerar Recebimento" : "Simulação de Recebimento"}{" "}
              {transaction_config?.batch ? "em Lote" : ""}
            </>
          ) : transaction_config?.type === "despesa" ? (
            <>{canSubmit ? "Gerar Pagamento" : "Simulação de Pagamento"}</>
          ) : transaction_config?.type === "reboleto" ? (
            <>{canSubmit ? "Renegociação de Pagamento(s)" : "Simulação de Renegociação de Pagamento(s)"}</>
          ) : transaction_config?.type === "gerar-diferenca" ? (
            <>{canSubmit ? "Gerar Diferença" : "Simulação de Diferença"}</>
          ) : (
            <>{canSubmit ? "Gerar Financeiro" : "Simulação de Financeiro"}</>
          )}
        </ModalCustom.Title>
      </ModalCustom.Header>
      <ModalCustom.Body>
        {!isLoadingStartup ? (
          <>
            {!canSubmit && (
              <p className="small-label mb-2">
                Você não tem permissão para efetivar esta operação, mas pode montar a simulação para conferir os
                valores. Nenhuma alteração será salva.
              </p>
            )}
            <Row className="d-none">
              <Col sm={4}>
                <span>
                  Tipo de Transação <Required />
                </span>
                <Slct
                  options={[
                    { label: "Recebimento", value: "receita" },
                    { label: "Pagamento", value: "despesa" },
                  ]}
                  value={transaction_config?.type}
                  isDisabled={true}
                  onChange={() => {}}
                />
              </Col>
              {transaction_config?.type === "receita" && (
                <>
                  <Col sm={4}>
                    <span>
                      Recebimento em Lote? <Required />
                    </span>
                    <Slct
                      options={[
                        { label: "Não", value: null },
                        { label: "Sim", value: true },
                      ]}
                      value={transaction_config?.batch || null}
                      isDisabled={true}
                      onChange={() => {}}
                    />
                  </Col>
                </>
              )}
            </Row>
            <Row>
              {transaction_config?.type === "despesa" && (
                <>
                  <Col sm={6}>
                    <span>
                      Pagamento referente à <Required />
                    </span>
                    <Slct
                      options={[
                        { label: "Turma", value: null },
                        { label: "Fornecedor", value: "supplier" },
                      ]}
                      value={selects?.payment_to_supplier}
                      isDisabled={!!pre_load?.payment_to_supplier || !!isWaitingResponse}
                      onChange={__ev => {
                        setselects({
                          ...selects,
                          payment_to_supplier: __ev.value,
                          commercial_id: null,
                          suppier_id: null,
                        });
                      }}
                      placeholder="..."
                    />
                  </Col>
                </>
              )}

              {(transaction_config?.type === "receita" ||
                (transaction_config?.type === "despesa" && !selects?.payment_to_supplier)) && (
                <Col sm={6}>
                  <span>
                    Turma <Required />
                  </span>
                  <Slct
                    required={true}
                    options={commercials || []}
                    value={selects?.commercial_id}
                    isDisabled={!!pre_load?.commercial_id || !!isWaitingResponse}
                    onChange={__ev => {
                      setselects({
                        ...selects,
                        commercial_id: __ev.value,
                        trainee_group: null,
                        trainee_ids: !!transaction_config?.batch ? [] : null,
                      });
                      setpackageData();
                      setpackageSelects();
                    }}
                    placeholder="..."
                  />
                </Col>
              )}
              {transaction_config?.type === "despesa" && selects?.payment_to_supplier === "supplier" && (
                <Col sm={6}>
                  <span>
                    Fornecedor <Required />
                  </span>
                  <Slct
                    required={true}
                    options={suppliers || []}
                    value={selects?.suppier_id}
                    isDisabled={!!pre_load?.suppier_id || !!isWaitingResponse}
                    onChange={__ev => {
                      setselects({
                        ...selects,
                        suppier_id: __ev.value,
                      });
                    }}
                    placeholder="..."
                  />
                </Col>
              )}
              {transaction_config?.type === "receita" && !!transaction_config?.batch && (
                <Col sm={6}>
                  <span>
                    Grupos de Formandos <Required />
                  </span>
                  {!isLoadingTraineeGroups ? (
                    <Slct
                      required={true}
                      options={traineeGroups || []}
                      value={selects?.trainee_group}
                      isDisabled={!!pre_load?.trainee_group || !!isWaitingResponse}
                      onChange={__ev => {
                        setselects({
                          ...selects,
                          trainee_group: __ev.value,
                          trainee_ids: [],
                        });
                      }}
                      placeholder="..."
                    />
                  ) : (
                    <Loading small />
                  )}
                </Col>
              )}
              {transaction_config?.type === "receita" && (
                <Col className={!!transaction_config?.batch ? "mt-2" : ""}>
                  <span>
                    Formando{!!transaction_config?.batch && "s"} <Required />
                  </span>
                  <Slct
                    required={true}
                    isMulti={!!transaction_config?.batch}
                    options={
                      !transaction_config?.batch
                        ? commercials?.find(__commercial => __commercial.value === selects?.commercial_id)?.trainees ||
                          []
                        : traineeGroups?.find(__trainee_group => __trainee_group.value === selects?.trainee_group)
                            ?.trainees || []
                    }
                    value={
                      !transaction_config?.batch
                        ? (selects?.trainee_ids?.[0] ?? null)
                        : selects?.trainee_ids || []
                    }
                    isDisabled={!!pre_load?.trainee_ids || !!isWaitingResponse}
                    onChange={__ev => {
                      setselects({
                        ...selects,
                        trainee_ids: !transaction_config?.batch ? [__ev.value] : __ev,
                      });
                    }}
                    placeholder="..."
                  />
                </Col>
              )}
            </Row>
            {(!!isLoadingTraineesData || !!isLoadingTraineeGroups) && !transaction_config?.batch ? (
              <Loading className="mt-2 mb-2" />
            ) : (
              <>
                {((transaction_config?.type === "receita" && !transaction_config?.batch && !!packageData) ||
                  (transaction_config?.type === "receita" &&
                    !!transaction_config?.batch &&
                    !!selects?.trainee_group &&
                    selects?.trainee_ids.length > 0 &&
                    !!packageData)) && (
                  <>
                    {packageData?.packages?.length > 0 && (
                      <>
                        <div className="divisor mt-3 mb-3"></div>
                        <Row className="mt-2">
                          <Col>
                            <h2 className="mini-title">Pacotes:</h2>
                            <p className="small-label mb-2">
                              Escolha um pacote entre os disponibilizados pela turma para adicionar ao formando.
                            </p>
                          </Col>
                        </Row>
                        {packageData?.packages?.map(__package => {
                          const _is_checked = packageSelects?.package === __package?.id || false;

                          const _can_change =
                            !transaction_config?.batch ||
                            (!!transaction_config?.batch && selects?.trainee_group === "0") ||
                            false;

                          if (!_can_change && !_is_checked) return null;

                          return (
                            <PackageSelectionCard
                              key={__package?.id}
                              checked={_is_checked}
                              disabled={!_can_change}
                              name={__package.name}
                              total={formatReal(__package.value_per_trainee_original || 0)}
                              onChange={__ev => {
                                if (_can_change && !isWaitingResponse) {
                                  setpackageSelects({
                                    ...packageSelects,
                                    package: __ev.target.checked ? __package?.id : null,
                                  });
                                }
                              }}
                            >
                              <PackageItemsCloud
                                items={[
                                  ...[...__package.events]
                                    .sort((__a, __b) => __a.name.localeCompare(__b.name))
                                    .map(__event => ({
                                      key: `event-${__event.id}`,
                                      label: __event.name,
                                      value: formatReal(__event.cost.total / __event.number_trainees.total || 0),
                                      note: buildInviteNote(__event.id),
                                    })),
                                  {
                                    key: "advisory",
                                    label: "Assessoria",
                                    value: formatReal(__package?.advisory || 0),
                                    tone: "resource",
                                  },
                                  {
                                    key: "reserve_fund",
                                    label: "Fundo de Reserva",
                                    value: formatReal(__package?.reserve_fund || 0),
                                    tone: "resource",
                                  },
                                ]}
                              />
                              {renderEventInvitesTable(__package.events)}
                            </PackageSelectionCard>
                          );
                        })}
                      </>
                    )}
                    {packageData?.singles?.length > 0 && (
                      <>
                        <div className="divisor mt-3 mb-3"></div>
                        <Row className="mt-2">
                          <Col>
                            <h2 className="mini-title">Eventos Avulsos:</h2>
                            <p className="small-label mb-2">
                              Você também pode adicionar eventos avulsos disponibilizados pela turma para adicionar ao
                              formando.
                            </p>
                          </Col>
                        </Row>
                        {packageData?.singles?.map(__single => {
                          const _is_checked =
                            packageSelects?.singles?.some(__single_select => __single_select === __single?.id) ||
                            false;

                          const _can_change =
                            !transaction_config?.batch ||
                            (!!transaction_config?.batch && selects?.trainee_group === "0") ||
                            false;

                          if (!_can_change && !_is_checked) return null;

                          return (
                            <PackageSelectionCard
                              key={__single?.id}
                              checked={_is_checked}
                              disabled={!_can_change}
                              name={__single.name}
                              total={formatReal(__single.value_per_trainee_original || 0)}
                              onChange={__ev => {
                                if (_can_change && !isWaitingResponse) {
                                  let _singles = [...packageSelects?.singles];
                                  if (__ev.target.checked) {
                                    _singles.push(__single?.id);
                                  } else {
                                    let _i = _singles.indexOf(__single?.id);
                                    _singles.splice(_i, 1);
                                  }
                                  setpackageSelects({
                                    ...packageSelects,
                                    singles: _singles,
                                  });
                                }
                              }}
                            >
                              <PackageItemsCloud
                                items={[
                                  {
                                    key: "event",
                                    label: __single?.event?.name,
                                    value: formatReal(__single.value_per_trainee_original || 0),
                                    note: buildInviteNote(__single?.event?.id),
                                  },
                                  {
                                    key: "advisory",
                                    label: "Assessoria",
                                    value: formatReal(__single?.advisory || 0),
                                    tone: "resource",
                                  },
                                  {
                                    key: "reserve_fund",
                                    label: "Fundo de Reserva",
                                    value: formatReal(__single?.reserve_fund || 0),
                                    tone: "resource",
                                  },
                                ]}
                              />
                              {renderEventInvitesTable([__single?.event])}
                            </PackageSelectionCard>
                          );
                        })}
                      </>
                    )}
                    <div className="divisor mt-3 mb-3"></div>
                    <Row className="mt-2">
                      <Col>
                        <h2 className="mini-title">Itens Adicionais:</h2>
                        <p className="small-label mb-2">Você também pode adicionar itens adicionais ao formando.</p>
                        {packageSelects?.aditional_items?.map((__item, __item_i) => (
                          <Accordion2 isHide={!!__item?.disabled}>
                            <Accordion2.Item eventKey={__item?.id ? `item-${__item?.id}` : null}>
                              <Accordion2.Header>
                                <div className="titleAccordion">
                                  <div className="d-flex align-items-center">
                                    <span>
                                      {__item?.quantity > 1 && `${__item?.quantity} `}
                                      {__item?.name}
                                    </span>
                                  </div>
                                </div>
                                <Accordion2.Extras>
                                  <p className="m-0">
                                    {formatReal(
                                      (__item?.quantity || 0) * (__item?.value || 0) + (__item?.convenience_fee || 0),
                                    )}
                                  </p>
                                </Accordion2.Extras>
                              </Accordion2.Header>
                              <Accordion2.Body>
                                <Row>
                                  <Col>
                                    <span>
                                      Centro de Custo <Required />
                                    </span>
                                    <Slct
                                      required={!__item?.disabled}
                                      isDisabled={!!isWaitingResponse}
                                      options={costCenters || []}
                                      value={__item?.cost_center_id}
                                      onChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            cost_center_id: __ev.value,
                                            expense_plan_id: null,
                                            expense_id: null,
                                          },
                                        });
                                      }}
                                      placeholder="..."
                                    />
                                  </Col>
                                  <Col>
                                    <span>
                                      Plano de Conta <Required />
                                    </span>
                                    <Slct
                                      required={!__item?.disabled}
                                      isDisabled={!!isWaitingResponse}
                                      options={
                                        costCenters?.find(
                                          __cost_center => __cost_center.value === __item?.cost_center_id,
                                        )?.expensePlan || []
                                      }
                                      value={__item?.expense_plan_id}
                                      onChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            expense_plan_id: __ev.value,
                                            expense_id: null,
                                          },
                                        });
                                      }}
                                      placeholder="..."
                                    />
                                  </Col>
                                  <Col>
                                    <span>
                                      Conta <Required />
                                    </span>
                                    <Slct
                                      required={!__item?.disabled}
                                      isDisabled={!!isWaitingResponse}
                                      options={
                                        costCenters
                                          ?.find(__cost_center => __cost_center.value === __item.cost_center_id)
                                          ?.expensePlan?.find(
                                            __expense_plan => __expense_plan.value === __item.expense_plan_id,
                                          )?.expenses || []
                                      }
                                      value={__item?.expense_id}
                                      onChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            expense_id: __ev.value,
                                          },
                                        });
                                      }}
                                      placeholder="..."
                                    />
                                  </Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col>
                                    <span>
                                      Nome da Cobrança <Required />
                                    </span>
                                    <InputText
                                      required={!__item?.disabled}
                                      disabled={!!isWaitingResponse}
                                      value={__item?.name}
                                      onChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            name: __ev.target.value,
                                          },
                                        });
                                      }}
                                    />
                                  </Col>
                                  <Col>
                                    <label>Evento</label>
                                    <Slct
                                      isDisabled={!!isWaitingResponse}
                                      options={packageData?.events || []}
                                      value={__item?.event_id}
                                      onChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            event_id: __ev.value,
                                            resource_id: null,
                                          },
                                        });
                                      }}
                                      placeholder="..."
                                    />
                                  </Col>
                                  <Col>
                                    <label>Recurso</label>
                                    <Slct
                                      isDisabled={!!isWaitingResponse}
                                      options={
                                        packageData?.events?.find(__event => __event.value === __item?.event_id)
                                          ?.resources || []
                                      }
                                      value={__item?.resource_id}
                                      onChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            resource_id: __ev.value,
                                          },
                                        });
                                      }}
                                      placeholder="..."
                                    />
                                  </Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col>
                                    <label>Descrição</label>
                                    <InputText
                                      disabled={!!isWaitingResponse}
                                      value={__item?.description}
                                      onChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            description: __ev.target.value,
                                          },
                                        });
                                      }}
                                    />
                                  </Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col>
                                    <label>
                                      Quantidade <Required />
                                    </label>
                                    <IntegerNumber
                                      required={!__item?.disabled}
                                      disabled={!!isWaitingResponse}
                                      value={__item?.quantity}
                                      valueChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            quantity: __ev,
                                          },
                                        });
                                      }}
                                    />
                                  </Col>
                                  <Col>
                                    <label>
                                      Valor (Por Unidade) <Required />
                                    </label>
                                    <Currency
                                      required={!__item?.disabled}
                                      disabled={!!isWaitingResponse}
                                      value={__item?.value}
                                      valueChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            value: __ev,
                                          },
                                        });
                                      }}
                                    />
                                  </Col>
                                  <Col>
                                    <label>Taxa de Conveniência</label>
                                    <Currency
                                      disabled={!!isWaitingResponse}
                                      value={__item?.convenience_fee}
                                      valueChange={__ev => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            convenience_fee: __ev,
                                          },
                                        });
                                      }}
                                    />
                                  </Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col className="text-end">
                                    <Button
                                      disabled={isWaitingResponse}
                                      size="sm"
                                      color="error"
                                      onClick={() => {
                                        setpackageSelectsArray({
                                          key: "aditional_items",
                                          index: __item_i,
                                          data: {
                                            disabled: true,
                                          },
                                        });
                                      }}
                                    >
                                      Remover {__item?.name}
                                    </Button>
                                  </Col>
                                </Row>
                              </Accordion2.Body>
                            </Accordion2.Item>
                          </Accordion2>
                        ))}
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Button
                          disabled={isWaitingResponse}
                          size="sm"
                          color="success"
                          onClick={() => {
                            setpackageSelects({
                              ...packageSelects,
                              aditional_items: packageSelects?.aditional_items?.concat([{}]),
                            });
                          }}
                        >
                          Adicionar Item
                        </Button>
                      </Col>
                    </Row>
                    <div className="divisor mt-3 mb-3"></div>
                    <Row className="mt-2">
                      <Col>
                        <h2 className="mini-title">Bônus & Promoções:</h2>
                        <p className="small-label mb-2">
                          Você pode adicionar descontos <b>fixos</b> ao formando. Você pode, por exemplo, criar
                          descontos para a Comissão.
                        </p>
                        {packageSelects?.discounts?.filter(__discount => !__discount.disabled)?.length > 0 && (
                          <TableResponsive
                            table={{
                              head: [
                                { data: "Valor", table_width: "200px" },
                                { data: "Descrição" },
                                { data: "", table_width: "80px" },
                              ],
                              body:
                                packageSelects?.discounts?.map((__discount, __discount_i) => ({
                                  className: !!__discount.disabled ? "d-none" : "",
                                  data: [
                                    {
                                      element: (
                                        <Currency
                                          disabled={!!isWaitingResponse}
                                          value={__discount?.value}
                                          valueChange={__ev => {
                                            setpackageSelectsArray({
                                              key: "discounts",
                                              index: __discount_i,
                                              data: {
                                                value: __ev,
                                              },
                                            });
                                          }}
                                        />
                                      ),
                                    },
                                    {
                                      element: (
                                        <InputText
                                          disabled={!!isWaitingResponse}
                                          value={__discount?.description}
                                          onChange={__ev => {
                                            setpackageSelectsArray({
                                              key: "discounts",
                                              index: __discount_i,
                                              data: {
                                                description: __ev.target.value,
                                              },
                                            });
                                          }}
                                        />
                                      ),
                                    },
                                    {
                                      element: (
                                        <div className="text-end">
                                          <Button
                                            disabled={!!isWaitingResponse}
                                            size="sm"
                                            color="error"
                                            onClick={() => {
                                              setpackageSelectsArray({
                                                key: "discounts",
                                                index: __discount_i,
                                                data: {
                                                  disabled: true,
                                                },
                                              });
                                            }}
                                          >
                                            Remover
                                          </Button>
                                        </div>
                                      ),
                                    },
                                  ],
                                })) || [],
                            }}
                          />
                        )}
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col>
                        <Button
                          disabled={!!isWaitingResponse}
                          size="sm"
                          color="success"
                          onClick={() => {
                            setpackageSelects({
                              ...packageSelects,
                              discounts: packageSelects?.discounts?.concat([{}]),
                            });
                          }}
                        >
                          Adicionar Bônus
                        </Button>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}
            {!isWaitingResponse && transaction_config?.type === "reboleto" && (
              <>
                <Row>
                  <Col>
                    <h2 className="mini-title">Pagamentos:</h2>
                    <p className="small-label mb-2">Lista de pagamentos que serão renegociados.</p>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <SlipsTable date_end={packageData?.date_end} slips={slips} no_actions={true} />
                  </Col>
                </Row>
              </>
            )}
            {!isWaitingResponse &&
              (transaction_config?.type === "gerar-diferenca" ||
                (transaction_config?.type === "receita" && selects?.trainee_ids?.length > 0 && !!packageSelects) ||
                (transaction_config?.type === "despesa" &&
                  ((!selects?.payment_to_supplier && selects?.commercial_id) ||
                    (selects?.payment_to_supplier === "supplier" && selects?.suppier_id))) ||
                (transaction_config?.type === "reboleto" && slips?.length > 0)) && (
                <>
                  {transaction_config?.type === "receita" && <div className="divisor mt-3 mb-3"></div>}
                  <Row>
                    <Col>
                      <h2 className="mini-title">Negociação:</h2>
                      <p className="small-label mb-2">
                        Informe todos os dados e valores para prosseguir com a Negociação.
                      </p>
                    </Col>
                  </Row>
                  {costCenters?.length > 0 &&
                    !willRefund &&
                    (transaction_config?.type === "receita" ||
                      transaction_config?.type === "despesa" ||
                      transaction_config?.type === "gerar-diferenca") && (
                      <Row>
                        <Col>
                          <span>
                            Centro de Custo <Required />
                          </span>
                          <Slct
                            required={true}
                            options={costCenters || []}
                            value={selects?.cost_center_id}
                            isDisabled={!!pre_load?.cost_center_id || !!isWaitingResponse}
                            onChange={__ev => {
                              setselects({
                                ...selects,
                                cost_center_id: __ev.value,
                                expense_plan_id: null,
                                expense_id: null,
                              });
                            }}
                            placeholder="..."
                          />
                        </Col>
                        <Col>
                          <span>
                            Plano de Conta <Required />
                          </span>
                          <Slct
                            required={true}
                            options={
                              selects?.cost_center_id
                                ? costCenters?.find(__cost_center => __cost_center.value === selects.cost_center_id)
                                    ?.expensePlan || []
                                : []
                            }
                            value={selects?.expense_plan_id}
                            isDisabled={!!pre_load?.expense_plan_id || !!isWaitingResponse}
                            onChange={__ev => {
                              setselects({
                                ...selects,
                                expense_plan_id: __ev.value,
                                expense_id: null,
                              });
                            }}
                            placeholder="..."
                          />
                        </Col>
                        <Col>
                          <span>
                            Conta <Required />
                          </span>
                          <Slct
                            required={true}
                            options={
                              selects.expense_plan_id
                                ? costCenters
                                    ?.find(__cost_center => __cost_center.value === selects.cost_center_id)
                                    ?.expensePlan?.find(
                                      __expense_plan => __expense_plan.value === selects.expense_plan_id,
                                    )?.expenses || []
                                : []
                            }
                            value={selects?.expense_id}
                            isDisabled={!!pre_load?.expense_id || !!isWaitingResponse}
                            onChange={__ev => {
                              setselects({
                                ...selects,
                                expense_id: __ev.value,
                              });
                            }}
                            placeholder="..."
                          />
                        </Col>
                      </Row>
                    )}
                  {!willRefund && (
                    <>
                      <Row className="mt-2">
                    <Col sm={4}>
                      <span>
                        Valor Total <Required />
                      </span>
                      <Currency
                        required={true}
                        allowNegative={false}
                        disabled={
                          transaction_config?.type === "receita" ||
                          transaction_config?.type === "reboleto" ||
                          !!isWaitingResponse
                        }
                        value={valueNegotiation}
                        valueChange={__ev => {
                          setvalueNegotiation(__ev);
                        }}
                      />
                    </Col>
                    <Col sm={4}>
                      <span>
                        Tipo de Pagamento <Required />
                      </span>
                      <Slct
                        isDisabled={!!isWaitingResponse}
                        required={true}
                        options={[
                          { value: "avista", label: "À Vista" },
                          { value: "parcelado", label: "A Prazo / Parcelado" },
                        ]}
                        value={values?.payment_type}
                        onChange={__ev => {
                          setvalues({
                            ...values,
                            payment_type: __ev.value,
                            payment_method: null,
                            has_entry: false,
                            quota_value: 1,
                            first_quota_date: __ev.value === "avista" ? new Date() : null,
                          });
                        }}
                        placeholder="..."
                      />
                    </Col>
                    <Col sm={4}>
                      <span>
                        Método do Pagamento <Required />
                      </span>
                      <Slct
                        isDisabled={!!isWaitingResponse}
                        required={true}
                        options={[
                          { value: "boleto", label: "Boleto" },
                          { value: "pix", label: "Pix" },
                          { value: "especie", label: "Espécie" },
                          ...(values.payment_type === "parcelado" ? [{ value: "cartao", label: "Cartão" }] : []),
                          ...(values.payment_type === "avista"
                            ? [{ value: "transferencia", label: "Transferência Bancária" }]
                            : []),
                        ]}
                        value={values?.payment_method}
                        onChange={__ev => {
                          setvalues({
                            ...values,
                            payment_method: __ev.value,
                          });
                        }}
                        placeholder="..."
                      />
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col sm={3}>
                      <span>Desconto (Negociação)</span>
                      <InputWSelect2
                        selected={values?.discount_type || "relative"}
                        setOption={__option => {
                          setvalues({
                            ...values,
                            discount_type: __option,
                          });
                          __option === "relative" &&
                            setvaluesPercent({
                              ...valuesPercent,
                              discount: null,
                            });
                        }}
                        options={[
                          {
                            label: "%",
                            value: "relative",
                            input: (
                              <Percent
                                disabled={!!isWaitingResponse}
                                value={valuesPercent?.discount}
                                isAllowed={__ev => {
                                  return !(__ev.floatValue > 100);
                                }}
                                valueChange={__ev => {
                                  setvaluesPercent({
                                    ...valuesPercent,
                                    discount: __ev,
                                  });
                                }}
                              />
                            ),
                          },
                          {
                            label: "R$",
                            value: "absolute",
                            input: (
                              <Currency
                                disabled={!!isWaitingResponse}
                                value={values?.discount}
                                valueChange={__ev => {
                                  setvalues({
                                    ...values,
                                    discount: __ev,
                                  });
                                }}
                              />
                            ),
                          },
                        ]}
                      />
                    </Col>
                    {values?.discount > 0 && (
                      <Col sm={3}>
                        <span>Descrição do Desconto</span>
                        <InputText
                          disabled={!!isWaitingResponse}
                          value={values?.discount_desc}
                          onChange={__ev => {
                            setvalues({
                              ...values,
                              discount_desc: __ev.target.value,
                            });
                          }}
                        />
                      </Col>
                    )}
                    <Col sm={3}>
                      <span>Taxas (Negociação)</span>
                      <InputWSelect2
                        selected={values?.fees_type || "relative"}
                        setOption={__option => {
                          setvalues({
                            ...values,
                            fees_type: __option,
                          });
                          __option === "relative" &&
                            setvaluesPercent({
                              ...valuesPercent,
                              fees: null,
                            });
                        }}
                        options={[
                          {
                            label: "%",
                            value: "relative",
                            input: (
                              <Percent
                                disabled={!!isWaitingResponse}
                                value={valuesPercent?.fees}
                                isAllowed={__ev => {
                                  return !(__ev.floatValue > 100);
                                }}
                                valueChange={__ev => {
                                  setvaluesPercent({
                                    ...valuesPercent,
                                    fees: __ev,
                                  });
                                }}
                              />
                            ),
                          },
                          {
                            label: "R$",
                            value: "absolute",
                            input: (
                              <Currency
                                disabled={!!isWaitingResponse}
                                value={values?.fees}
                                valueChange={__ev => {
                                  setvalues({
                                    ...values,
                                    fees: __ev,
                                  });
                                }}
                              />
                            ),
                          },
                        ]}
                      />
                    </Col>
                    {values?.fees > 0 && (
                      <Col sm={3}>
                        <span>Descrição das Taxas</span>
                        <InputText
                          disabled={!!isWaitingResponse}
                          value={values?.fees_desc}
                          onChange={__ev => {
                            setvalues({
                              ...values,
                              fees_desc: __ev.target.value,
                            });
                          }}
                        />
                      </Col>
                    )}
                      </Row>
                    </>
                  )}
                  <Row className="mt-3">
                    <Col sm={6}>
                      {!willRefund ? (
                        <>
                          <p className="mini-title mb-2">
                            <b>Observações:</b>
                          </p>
                          <p className="small-label mb-2">
                            Ao menos uma parcela não poderá ser editada, pois ela receberá o restante do Valor Total.
                          </p>
                          <p className="small-label mb-2">
                            O valor das parcelas não poderão ser editadas caso o Método de Pagamento seja{" "}
                            <b>Cartão</b>.
                          </p>
                          {(selects?.trainee_ids?.length > 0 || selects?.due_date) && (
                            <>
                              <p className="small-label mb-2">
                                Caso o Intervalo selecionado seja <b>Mensal</b>, as parcelas serão automaticamente
                                lançadas no dia de escolha do formando.{" "}
                                {(!!packageSelects?.due_date || selects?.due_date) && (
                                  <b>(Dia {selects?.due_date || packageSelects?.due_date})</b>
                                )}
                              </p>
                            </>
                          )}
                          {(!!packageData?.date_end || selects?.date_end) && (
                            <p className="small-label mb-2">
                              Não aconselhamos que existam parcelas após a data da Última Parcela da Turma.{" "}
                              <b>({formatDate(packageData?.date_end || selects?.date_end, 7)})</b>
                            </p>
                          )}
                          {values?.fees > 0 && (
                            <p className="small-label mb-2">
                              Não pode existir um valor de parcela menor que o valor das taxas fracionadas.{" "}
                              <b>({formatReal((values?.fees || 0) / quotas?.length)})</b>
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="mini-title mb-2">
                            <b>Observações:</b>
                          </p>
                          <p className="small-label mb-2">
                            Este reembolso é registrado como um <b>Título de Pagamento</b> — ele não transfere o
                            valor automaticamente. O pagamento em si (Pix, transferência, espécie, etc.) precisa ser
                            confirmado depois na tela de <b>Contas a Pagar</b>.
                          </p>
                          <p className="small-label mb-2">
                            Centro de Custo, Plano de Conta e Conta classificam esse reembolso como uma despesa da
                            empresa, da mesma forma que qualquer outro Título de Pagamento.
                          </p>
                          <p className="small-label mb-2">
                            A Descrição ajuda a identificar esse reembolso depois, na listagem de Contas a Pagar.
                          </p>
                          <p className="small-label mb-2">
                            Ao menos uma parcela do parcelamento não poderá ser editada — ela receberá
                            automaticamente o restante do valor do reembolso.
                          </p>
                        </>
                      )}
                    </Col>
                    <Col sm={6}>
                      <Results
                        rows={[
                          ...(transaction_config?.type === "receita"
                            ? [
                                {
                                  label: "Pacote Contratado",
                                  value: formatReal(values?.trainee?.package_value),
                                },
                                {
                                  label: "Avulsos Contratados",
                                  value: formatReal(values?.trainee?.singles_value),
                                },
                                {
                                  label: "Itens Adicionais Contratados",
                                  value: formatReal(values?.trainee?.aditional_items_value),
                                },
                                {
                                  label: "Convites/Ingressos Extras",
                                  value: formatReal(values?.trainee?.event_invites_value),
                                },
                                {
                                  label: <b>Total Contratado</b>,
                                  value: <b>{formatReal(values?.trainee?.contracted_value)}</b>,
                                  type: "dark",
                                },
                                {
                                  label: "Bônus e Promoções (Contratado)",
                                  value: `- ${formatReal(values?.trainee?.discounts)}`,
                                },
                                ...(!transaction_config?.batch
                                  ? [
                                      {
                                        label: "Pagamentos Efetuados (Contratado)",
                                        value: `- ${formatReal(values?.trainee?.payments_made)}`,
                                      },
                                      ...(values?.trainee?.refunds_to_trainee > 0
                                        ? [
                                            {
                                              label: "Reembolsos ao Formando",
                                              value: `+ ${formatReal(values?.trainee?.refunds_to_trainee)}`,
                                            },
                                          ]
                                        : []),
                                      {
                                        label: "Taxas e Descontos de Pagamentos Efetuados",
                                        value: `${values?.trainee?.payments_made_adjusts > 0 ? "-" : "+"} ${formatReal(
                                          Math.abs(values?.trainee?.payments_made_adjusts),
                                        )}`,
                                      },
                                      {
                                        label: "Resultado de Aportes e Retiradas",
                                        value: `${
                                          values?.trainee?.contributions_benefits <= 0 ? "+" : "-"
                                        } ${formatReal(Math.abs(values?.trainee?.contributions_benefits))}`,
                                      },
                                      {
                                        label: "Multas e Juros (Atrasos)",
                                        value: `+ ${formatReal(values?.trainee?.slips_fines_fees)}`,
                                      },
                                    ]
                                  : []),
                              ]
                            : []),
                          ...(transaction_config?.type === "reboleto"
                            ? [
                                {
                                  label: <b>Valor do(s) Pagamento(s)</b>,
                                  value: <b>{formatReal(values?.slips?.slips_value)}</b>,
                                  type: "dark",
                                },
                                {
                                  label: "Multas & Juros (Atrasos)",
                                  value: `+ ${formatReal(values?.slips?.slips_fines_fees)}`,
                                },
                              ]
                            : []),
                          {
                            label: <b>Total Débito</b>,
                            value: <b>{formatReal(valueNegotiation)}</b>,
                            type: "dark",
                          },
                          {
                            label: "Desconto (Negociação)",
                            value: `- ${formatReal(values?.discount)}`,
                          },
                          {
                            label: "Taxas (Negociação)",
                            value: `+ ${formatReal(values?.fees)}`,
                          },
                          {
                            label: <b>Resultado Débito</b>,
                            value: <b>{formatReal(values?.result)}</b>,
                            type: true ? "dark" : "",
                          },
                          ...(willRefund
                            ? [
                                {
                                  label: <b>Reembolso ao Formando</b>,
                                  value: <b>{formatReal(Math.abs(rawValueTotal))}</b>,
                                  type: "dark",
                                },
                              ]
                            : []),
                        ]}
                      />
                    </Col>
                  </Row>
                  {!willRefund && values?.payment_type === "parcelado" && !!values?.payment_method && (
                    <>
                      <Row>
                        <Col>
                          <div className="divisor mt-3 mb-3"></div>
                          <h2 className="mini-title">Parcelamento:</h2>
                          <p className="small-label mb-2">
                            Você pode customizar os valores e datas do parcelamentos. Deixe também algum comentário caso
                            precise recordar de algo relacionada a negociação da parcela.
                          </p>
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col sm={3}>
                          <span>
                            Parcelamento <Required />
                          </span>
                          <IntegerNumber
                            disabled={!!isWaitingResponse}
                            required={true}
                            value={values?.quota_value}
                            valueChange={_ev => {
                              setvalues({
                                ...values,
                                quota_value: _ev,
                              });
                            }}
                            onBlur={__ev => {
                              if (!(__ev.target?.value > 0)) {
                                setvalues({
                                  ...values,
                                  quota_value: 1,
                                });
                              }
                            }}
                            allowNegative={false}
                            maxlength="2"
                            placeholder="0"
                          />
                        </Col>
                        <Col sm={3}>
                          {(() => {
                            const _is_receita =
                              (transaction_config?.type === "receita" ||
                                transaction_config?.type === "reboleto" ||
                                transaction_config?.type === "gerar-recebimento") &&
                              !(values?.quota_interval > 0);
                            return (
                              <>
                                <span>
                                  Primeiro Pagamento <Required />
                                </span>
                                <DatePicker
                                  disabled={!!isWaitingResponse}
                                  required={true}
                                  selected={values?.first_quota_date}
                                  dateFormat={_is_receita ? "MM/yyyy" : "dd/MM/yyyy"}
                                  placeholder={_is_receita ? "--/----" : "--/--/----"}
                                  changeDate={__ev => {
                                    setvalues({
                                      ...values,
                                      first_quota_date: __ev,
                                    });
                                  }}
                                  minDate={_is_receita ? new Date() : null}
                                />
                              </>
                            );
                          })()}
                        </Col>
                        <Col sm={3}>
                          <div className="position-relative">
                            <span>Intervalo</span>
                            {values?.quota_interval === null || values?.quota_interval === undefined ? (
                              <input type="text" disabled="true" value="Mensal" />
                            ) : (
                              <IntegerNumber
                                disabled={!!isWaitingResponse}
                                value={values?.quota_interval}
                                valueChange={__ev => {
                                  setvalues({
                                    ...values,
                                    quota_interval: parseInt(__ev) || "",
                                  });
                                }}
                                onBlur={__ev => {
                                  if (!__ev?.target?.value || __ev?.target?.value?.[0] === "0") {
                                    setvalues({
                                      ...values,
                                      quota_interval: null,
                                    });
                                  }
                                }}
                                allowNegative={false}
                                placeholder="0 dias"
                                suffix={" dias"}
                              />
                            )}
                            <label className="im-checkbox-2">
                              <Checkbox
                                checked={!!values.quota_interval}
                                onChange={__ev => {
                                  if (!isWaitingResponse)
                                    setvalues({
                                      ...values,
                                      quota_interval: __ev.target.checked ? 30 : null,
                                    });
                                }}
                              />
                              <span>Em Dias</span>
                            </label>
                          </div>
                        </Col>
                        <Col sm={3}>
                          <span>Possui Entrada?</span>
                          <Slct
                            isDisabled={!!isWaitingResponse}
                            options={[
                              { value: false, label: "Não" },
                              { value: true, label: "Sim" },
                            ]}
                            value={values?.has_entry || false}
                            onChange={__ev => {
                              setvalues({
                                ...values,
                                has_entry: __ev.value,
                              });
                            }}
                            placeholder="..."
                          />
                        </Col>
                      </Row>
                      {quotas.length > 0 && (
                        <Row className="mt-4">
                          <Col>
                            <TableResponsive
                              table={{
                                head: [
                                  { data: "Editar" },
                                  { data: "Valor *" },
                                  {
                                    data: values?.payment_type === "avista" ? "Pagamento *" : "Vencimento *",
                                  },
                                  { data: "Comentário" },
                                ],
                                body:
                                  quotas?.map((__quota, __quota_i) => {
                                    const _is_batch =
                                      !!transaction_config?.batch && !(values?.quota_interval > 0) && !__quota.is_entry;
                                    const _is_receita =
                                      transaction_config?.type === "receita" || transaction_config?.type === "reboleto";
                                    return {
                                      data: [
                                        {
                                          element: (
                                            <div className="d-flex">
                                              {((values?.payment_method !== "cartao" &&
                                                !__quota.is_entry &&
                                                quotas?.filter(__quota => !__quota.editable && !__quota.is_entry)
                                                  ?.length > 1) ||
                                                (!!__quota.editable && !__quota.is_entry)) && (
                                                <Checkbox
                                                  checked={__quota.editable}
                                                  onChange={__ev => {
                                                    if (!isWaitingResponse) {
                                                      let _quotas = [...quotas];
                                                      _quotas[__quota_i].editable = __ev.target.checked;
                                                      setquotas(_quotas);
                                                    }
                                                  }}
                                                />
                                              )}
                                              <span style={{ whiteSpace: "nowrap" }} className="ml-2">
                                                {__quota.label}
                                              </span>
                                            </div>
                                          ),
                                        },
                                        {
                                          element: (
                                            <Currency
                                              required={true}
                                              disabled={!__quota.editable || !!isWaitingResponse}
                                              value={__quota.value || ""}
                                              allowNegative={false}
                                              onBlur={__ev => {
                                                let _value =
                                                  parseFloat(
                                                    __ev?.target?.value
                                                      ?.replace("R$ ", "")
                                                      ?.replaceAll(".", "")
                                                      ?.replaceAll(",", "."),
                                                  ) || 0;
                                                !__quota.is_entry && checkQuotaBlur(_value, __quota_i);
                                              }}
                                              isAllowed={__ev => {
                                                const _result = checkQuotaInput(__ev, __quota_i);
                                                return !!_result;
                                              }}
                                              valueChange={__ev => {
                                                if (!!__quota.editable) {
                                                  setquotas(
                                                    quotas.map((__quota, __i) =>
                                                      __i === __quota_i
                                                        ? {
                                                            ...__quota,
                                                            value: __ev,
                                                          }
                                                        : __quota,
                                                    ),
                                                  );
                                                }
                                              }}
                                            />
                                          ),
                                        },
                                        {
                                          element: (
                                            <DatePicker
                                              disabled={!!isWaitingResponse}
                                              required={true}
                                              selected={__quota?.due_date || null}
                                              dateFormat={_is_batch ? "MM/yyyy" : "dd/MM/yyyy"}
                                              placeholder={_is_batch ? "--/----" : "--/--/----"}
                                              changeDate={__ev => {
                                                let _quotas = [...quotas];
                                                _quotas[__quota_i].due_date = __ev;
                                                setquotas(_quotas);
                                              }}
                                              minDate={_is_receita ? new Date() : null}
                                              attention={
                                                (transaction_config?.type === "receita" ||
                                                  transaction_config?.type === "reboleto" ||
                                                  transaction_config?.type === "gerar-diferenca") &&
                                                __quota?.due_date > (packageData?.date_end || pre_load?.date_end)
                                              }
                                              attentionMsg={
                                                (transaction_config?.type === "receita" ||
                                                  transaction_config?.type === "reboleto" ||
                                                  transaction_config?.type === "gerar-diferenca") &&
                                                __quota?.due_date > (packageData?.date_end || pre_load?.date_end)
                                                  ? `Não aconselhamos que existam parcelas após a data da Última Parcela da Turma. (${formatDate(
                                                      packageData?.date_end || pre_load?.date_end,
                                                      7,
                                                    )})`
                                                  : ""
                                              }
                                            />
                                          ),
                                        },
                                        {
                                          element: (
                                            <InputText
                                              disabled={!!isWaitingResponse}
                                              value={__quota?.description}
                                              onChange={__ev => {
                                                let _quotas = [...quotas];
                                                _quotas[__quota_i].description = __ev.target.value;
                                                setquotas(_quotas);
                                              }}
                                            />
                                          ),
                                        },
                                      ],
                                      key: `quota-${__quota_i}`,
                                    };
                                  }) || [],
                              }}
                            />
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </>
              )}
            {willRefund && (
              <>
                <div className="divisor mt-3 mb-3"></div>
                <Row>
                  <Col>
                    <h2 className="mini-title">Reembolso ao Formando:</h2>
                    <p className="small-label mb-2">
                      O formando já pagou mais do que o novo total negociado — em vez de gerar uma cobrança, será
                      gerado um reembolso para a empresa pagar ao formando.
                    </p>
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col>
                    <StatusFlag status="Pagamento">Pagamento ao Formando (Reembolso)</StatusFlag>
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col>
                    <span>
                      Descrição do Reembolso <Required />
                    </span>
                    <InputText
                      required={true}
                      disabled={!!isWaitingResponse}
                      value={refundDescription}
                      onChange={__ev => setrefundDescription(__ev.target.value)}
                      placeholder="Ex: Reembolso por renegociação para pacote de menor valor"
                    />
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col sm={4}>
                    <span>
                      Centro de Custo <Required />
                    </span>
                    <Slct
                      required={true}
                      isDisabled={!!isWaitingResponse}
                      options={refundCostCenters || []}
                      value={refundSelects?.cost_center_id}
                      onChange={__ev => {
                        setrefundSelects({
                          ...refundSelects,
                          cost_center_id: __ev.value,
                          expense_plan_id: null,
                          expense_id: null,
                        });
                      }}
                      placeholder="..."
                    />
                  </Col>
                  <Col sm={4}>
                    <span>
                      Plano de Conta <Required />
                    </span>
                    <Slct
                      required={true}
                      isDisabled={!!isWaitingResponse}
                      options={
                        refundSelects?.cost_center_id
                          ? refundCostCenters?.find(
                              __cost_center => __cost_center.value === refundSelects.cost_center_id,
                            )?.expensePlan || []
                          : []
                      }
                      value={refundSelects?.expense_plan_id}
                      onChange={__ev => {
                        setrefundSelects({
                          ...refundSelects,
                          expense_plan_id: __ev.value,
                          expense_id: null,
                        });
                      }}
                      placeholder="..."
                    />
                  </Col>
                  <Col sm={4}>
                    <span>
                      Conta <Required />
                    </span>
                    <Slct
                      required={true}
                      isDisabled={!!isWaitingResponse}
                      options={
                        refundSelects?.expense_plan_id
                          ? refundCostCenters
                              ?.find(__cost_center => __cost_center.value === refundSelects.cost_center_id)
                              ?.expensePlan?.find(
                                __expense_plan => __expense_plan.value === refundSelects.expense_plan_id,
                              )?.expenses || []
                          : []
                      }
                      value={refundSelects?.expense_id}
                      onChange={__ev => {
                        setrefundSelects({
                          ...refundSelects,
                          expense_id: __ev.value,
                        });
                      }}
                      placeholder="..."
                    />
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col sm={4}>
                    <span>
                      Tipo de Pagamento (Reembolso) <Required />
                    </span>
                    <Slct
                      isDisabled={!!isWaitingResponse}
                      required={true}
                      options={[
                        { value: "avista", label: "À Vista" },
                        { value: "parcelado", label: "A Prazo / Parcelado" },
                      ]}
                      value={refundPaymentType}
                      onChange={__ev => {
                        setrefundPaymentType(__ev.value);
                        setrefundPaymentMethod(null);
                        setrefundQuotaConfig({
                          ...refundQuotaConfig,
                          quota_value: 1,
                          has_entry: false,
                          first_quota_date:
                            __ev.value === "avista" ? new Date() : refundQuotaConfig?.first_quota_date,
                        });
                      }}
                      placeholder="..."
                    />
                  </Col>
                  <Col sm={4}>
                    <span>
                      Método do Pagamento (Reembolso) <Required />
                    </span>
                    <Slct
                      isDisabled={!!isWaitingResponse}
                      required={true}
                      options={[
                        { value: "pix", label: "Pix" },
                        { value: "boleto", label: "Boleto" },
                        { value: "especie", label: "Espécie" },
                        { value: "cartao", label: "Cartão" },
                        { value: "transferencia", label: "Transferência" },
                      ]}
                      value={refundPaymentMethod}
                      onChange={__ev => setrefundPaymentMethod(__ev.value)}
                      placeholder="..."
                    />
                  </Col>
                </Row>
                {!!refundPaymentType && (
                  <Row className="mt-2">
                    <Col>
                      <QuotaEditor
                        result={Math.abs(rawValueTotal)}
                        quotas={refundQuotas}
                        setquotas={setrefundQuotas}
                        config={refundQuotaConfig}
                        setconfig={setrefundQuotaConfig}
                        paymentType={refundPaymentType}
                        disabled={!!isWaitingResponse}
                      />
                    </Col>
                  </Row>
                )}
              </>
            )}
          </>
        ) : (
          <Loading className="mb-2" />
        )}
      </ModalCustom.Body>
      {canSubmit && (
        <ModalCustom.Footer>
          <Button disabled={isWaitingResponse} loading={isLoadingRequest} onClick={sendFinancial}>
            {transaction_config?.type === "receita"
              ? "Gerar Recebimento"
              : transaction_config?.type === "reboleto"
                ? "Reemitir Boleto(s)"
                : transaction_config?.type === "gerar-diferenca"
                  ? "Gerar Diferença"
                  : "Gerar Negociação"}
          </Button>
        </ModalCustom.Footer>
      )}
    </ModalCustom>
  );
}
